import {
  SubconsoleEntities,
  SubconsoleData
} from "@/domain/entities/Subconsole";
import { Pagination } from "@/domain/entities/Pagination";
import { AxiosResponse } from "axios";
import {
  Handover,
  HandoverList,
  HandoverSttResult,
  SttPiece,
  SttDetailData,
  PieceDetail,
  ResponseUpdate,
  PartnerVendorList,
  HandoverDetail,
  SttData,
  FailedVendorStt,
  FailedVendorSttItem,
  HandoverFailedStt,
  RetryHandoverVendorResult,
  HandoverFailedSttItem,
} from "@/domain/entities/Handover";
import {
  ResponsePayload,
  ResponseSttDetail
} from "@/domain/entities/ResponsePayload";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { isArray } from "lodash";
import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";
import { PrintDataEntitas, SttProduct } from "@/domain/entities/Booking";
import { registerFontJsPdfOnPrint } from "./BookingMapper";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";
import { IS_STT_RTS } from "@/app/infrastructures/misc/Utils";

export class HandoverMapper {
  public convertListSubconsoleDataFromApi(
    res: AxiosResponse<any>
  ): SubconsoleEntities {
    const { data } = res;
    const subconsole: SubconsoleData[] = [];
    if (data.data.length === 0) {
      return new SubconsoleEntities(
        new Pagination(data.meta.page, data.meta.limit),
        []
      );
    } else {
      data.data.map((item: any) => {
        subconsole.push(
          new SubconsoleData(
            item.id,
            item.name,
            item.address,
            item.balance,
            item.code,
            item.lat,
            item.long,
            item.type,
            item.created_at
          )
        );
      });
    }
    return new SubconsoleEntities(
      new Pagination(data.meta.page, data.meta.limit),
      subconsole
    );
  }

  public convertHandovertListFromApi(res: AxiosResponse<any>) {
    const { data } = res;
    const handovers: Handover[] = [];
    if (data.data.length === 0 || data.data === null) {
      return new HandoverList(new Pagination(1, 10), []);
    } else {
      data.data.map((item: any) => {
        handovers.push(
          new Handover(
            item.handover_id,
            item.handover_destination_partner_name,
            item.handover_vendor_code,
            item.handover_total_stt,
            item.handover_total_piece,
            item.handover_total_gross_weight,
            item.handover_total_volume_weight,
            item.handover_created_at,
            item.handover_created_name,
            item.is_support_print_resi,
            item.vendor_order_status || "",
            item.is_view_detail_vendor_order || false,
            item.is_retry_available || false,
            (item.handover_status || "").trim().toUpperCase(),
            item.is_view_detail_handover || false,
            item.handover_total_stt_failed || 0,
            item.handover_vendor_name || ""
          )
        );
      });
    }
    return new HandoverList(
      new Pagination(data.meta.page, data.meta.limit, data.meta.total_records),
      handovers
    );
  }

  public convertHandoverFailedSttFromApi(
    res: AxiosResponse<any>
  ): HandoverFailedStt {
    const {
      data: { data }
    } = res;
    return new HandoverFailedStt({
      handoverId: data?.handover_id || 0,
      totalFailed: data?.total_failed || 0,
      failedStt: (data?.failed_stt || []).map(
        (item: any) =>
          new HandoverFailedSttItem({
            sttNo: item.stt_no,
            shipmentId: item.shipment_id || "",
            productType: item.product_type || "",
            commodityName: item.commodity_name || "",
            totalPiece: item.total_piece || 0,
            grossWeight: item.gross_weight || 0,
            volumeWeight: item.volume_weight || 0,
            reason: item.reason || "",
            reasonEn: item.reason_en || ""
          })
      )
    });
  }

  public convertFailedVendorSttFromApi(
    res: AxiosResponse<any>
  ): FailedVendorStt {
    const { data } = res;
    const body = data?.data;
    const meta = data?.meta;
    return new FailedVendorStt({
      vendorOrderStatus: body?.vendor_order_status || "",
      isViewDetailVendorOrder: body?.is_view_detail_vendor_order || false,
      isRetryAvailable: body?.is_retry_available || false,
      stt: (body?.stt || []).map(
        (item: any) =>
          new FailedVendorSttItem({
            sttNo: item.stt_no,
            sttVendorReferenceNo: item.stt_vendor_reference_no || "",
            status: item.status,
            message: item.message || item.reason || "",
            reason: item.reason || item.message || "",
            reasonEn: item.reason_en || "",
            retryAvailable: item.retry_available || false,
            lastStatusUpdatedAt: item.last_status_updated_at || ""
          })
      ),
      pagination: new Pagination(
        meta?.page || 1,
        meta?.limit || 100,
        meta?.total_records || 0
      )
    });
  }

  public convertSttDetailDataFromApi(
    res: AxiosResponse<any>
  ): ResponseSttDetail {
    const {
      data: { data }
    } = res;
    if (!data.is_allow_update_status)
      return new ResponseSttDetail(false, data.error_message, [], false);
    else
      return new ResponseSttDetail(
        data.is_allow_update_status,
        data.error_message,
        data.bag_stt !== null || data.bag_stt?.length
          ? data.bag_stt.map(
              (key: any) =>
                new SttDetailData(
                  key.stt_id,
                  key.stt_no,
                  key.stt_product_type,
                  key.stt_total_piece,
                  key.stt_origin_city_id,
                  key.stt_destination_city_id,
                  `${key.stt_destination_city_id} - ${key.stt_destination_city_name}`,
                  key.stt_wood_packing.toLowerCase() === "yes" ? "Ya" : "Tidak",
                  key.stt_commodity_code,
                  AccountController.accountData.accountIsForeign
                    ? key.stt_commodity_name_en
                    : key.stt_commodity_name,
                  key.stt_gross_weight,
                  key.stt_volume_weight,
                  key.stt_chargeable_weight,
                  key.stt_cod_amount,
                  key.stt_last_status_id,
                  key.booked_at,
                  key.piece.length > 0
                    ? key.piece.map(
                        (item: any) =>
                          new PieceDetail(
                            item.stt_piece_id,
                            item.stt_piece_gross_weight,
                            item.stt_piece_volume_weight,
                            `${key.stt_no}-${item.stt_piece_no}`,
                            item.stt_piece_last_status_id,
                            `${item.stt_piece_no}/${key.stt_total_piece}`
                          )
                      )
                    : [],
                  key.stt_elexys_no
                )
            )
          : new SttDetailData(
              data.stt.stt_id,
              data.stt.stt_no,
              data.stt.stt_product_type,
              data.stt.stt_total_piece,
              data.stt.stt_origin_city_id,
              data.stt.stt_destination_city_id,
              `${data.stt.stt_destination_city_id} - ${data.stt.stt_destination_city_name}`,
              data.stt.stt_wood_packing.toLowerCase() === "yes"
                ? "Ya"
                : "Tidak",
              data.stt.stt_commodity_code,
              AccountController.accountData.accountIsForeign
                ? data.stt.stt_commodity_name_en
                : data.stt.stt_commodity_name,
              data.stt.stt_gross_weight,
              data.stt.stt_volume_weight,
              data.stt.stt_chargeable_weight,
              data.stt.stt_cod_amount,
              data.stt.stt_last_status_id,
              data.stt.booked_at,
              data.stt.piece.length > 0
                ? data.stt.piece.map(
                    (item: any) =>
                      new PieceDetail(
                        item.stt_piece_id,
                        item.stt_piece_gross_weight,
                        item.stt_piece_volume_weight,
                        `${data.stt.stt_no}-${item.stt_piece_no}`,
                        item.stt_piece_last_status_id,
                        `${item.stt_piece_no}/${data.stt.stt_total_piece}`
                      )
                  )
                : [],
              data.stt.stt_elexys_no
            ),
        data.is_paid
      );
  }

  public convertHandoverManifestFromApi(
    res: AxiosResponse<any>
  ): HandoverDetail {
    const {
      data: { data }
    } = res;
    return new HandoverDetail({
      handoverManifestId: data.handover_id,
      handoverManifestTotalStt: data.handover_total_stt,
      handoverTotalPiece: data.handover_total_piece,
      handoverManifestTotalGrossWeight: data.handover_total_gross_weight,
      handoverManifestTotalVolumeWeight: data.handover_total_volume_weight,
      handoverPartnerName: data.handover_partner_name,
      handoverDestinationPartnerName:
        data.handover_destination_partner_name || data.handover_vendor_code,
      handoverVendorCode: data.handover_vendor_code,
      handoverArrivalCitycode: data.handover_arrival_city_code,
      handoverArrivalCityName: data.handover_arrival_city_name,
      handoverOriginCityCode: data.handover_origin_city_code,
      handoverOriginCityName: data.handover_origin_city_name,
      handoverCreatedBy: data.handover_created_by_name,
      handoverCreatedAt: data.handover_created_at,
      stt: data.stt.length
        ? data.stt.map(
            (item: any) =>
              new HandoverSttResult(
                item.stt_id,
                item.stt_no,
                item.stt_product_type,
                item.stt_total_piece,
                item.stt_destination_city_id,
                item.stt_destination_city_name,
                item.stt_wood_packing,
                item.stt_commodity_code,
                item.stt_commodity_name,
                item.stt_commodity_name_en,
                item.stt_gross_weight,
                item.stt_volume_weight,
                item.stt_chargeable_weight,
                item.stt_cod_amount,
                item.piece.map(
                  (val: any) =>
                    new SttPiece(
                      val.stt_piece_id,
                      val.stt_piece_gross_weight,
                      val.stt_piece_volume_weight,
                      val.stt_piece_no
                    )
                ),
                item.stt_origin_city_id,
                item.stt_origin_city_name,
                item.stt_elexys_no
              )
          )
        : [],
      handoverIsSupportPrintResi: data.is_support_print_resi,
      handoverStatus: data.handover_status
    });
  }

  public convertUpdateDataFromApi(result: AxiosResponse<any>): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message,
      data: new ResponseUpdate(
        data.data.handover_id,
        data.data.total_stt_success,
        data.data.total_stt_failed,
        data.data.stt_failed
      )
    });
  }

  public convertRetryVendorOrderFromApi(
    result: AxiosResponse<any>
  ): RetryHandoverVendorResult {
    const { data } = result;
    const item = data?.data || {};
    const sttNos: string[] = item.stt_nos || (item.stt_no ? [item.stt_no] : []);
    const sttNo: string = item.stt_no || (sttNos.length > 0 ? sttNos[0] : "");

    return new RetryHandoverVendorResult({
      success: data?.success ?? true,
      code: data?.code ?? 200,
      message: data?.message || "Success",
      handoverId: item.handover_id || 0,
      sttNo,
      sttNos,
      vendorCode: item.vendor_code || "",
      vendorOrderStatus: item.vendor_order_status || "",
      totalSttProcessing: item.total_stt_processing || sttNos.length || 0
    });
  }

  public convertPartnerVendorListDataFromApi(
    res: AxiosResponse<any>
  ): PartnerVendorList[] {
    const {
      data: { data }
    } = res;
    if (data.length === 0) return [];
    else
      return data.map(
        (item: any) =>
          new PartnerVendorList(
            item.code,
            item.partner_id,
            item.partner_name,
            item.type,
            item.city_code,
            item.vendor_name,
            item?.api_integration ? item.api_integration : false
          )
      );
  }

  public async convertHandoverReceiptFromApi(
    res: AxiosResponse<any>
  ): Promise<SttData[]> {
    const {
      data: { data }
    } = res;

    if (isArray(data)) {
      if (data.length && data?.[0]?.detail) {
        const detail = data[0]?.detail;

        const isInterpack =
          detail.stt_product_type.toUpperCase() === "INTERPACK";

        if (isInterpack) {
          await registerFontJsPdfOnPrint(detail);
        }
      }

      return data.map(
        (item: any) =>
          new SttData({
            ...convertSnakeToCamel(item),
            detail: new PrintDataEntitas(
              item.detail ? this.mapBasicInfo(item.detail) : undefined
            )
          })
      );
    }

    return [];
  }

  private mapBasicInfo(data: any) {
    const summaryPrintData = (data: any) => ({
      totalRates: data.stt_is_promo
        ? data.stt_shipping_surcharge_rate_after_discount +
          data.stt_publish_rate_after_discount +
          data.stt_destination_district_rate_after_discount +
          data.stt_origin_district_rate_after_discount
        : data.stt_shipping_surcharge_rate +
          data.stt_publish_rate +
          data.stt_destination_district_rate +
          data.stt_origin_district_rate,
      totalTariff: data.stt_is_promo
        ? data.stt_total_amount
        : data.stt_total_amount_before_discount
    });

    const { totalRates, totalTariff } = summaryPrintData(data);

    const extraReceipt = FlagsMedusa.config_extra_receipt.isEnabled();

    return new PrintDataEntitas({
      sttId: data.stt_id,
      sttNo: data.stt_no,
      sttProductType:
        data.stt_product_type?.toUpperCase() === "JUMBOPACKH2H"
          ? "JUMBOPACK"
          : data.stt_product_type,
      sttCreatedAt: data.stt_created_at,
      sttOriginCityId: data.stt_origin_city_id,
      sttOriginCityName: data.stt_origin_city_name,
      sttOriginCityTransit: data.stt_origin_city_transit,
      sttDestinationCityId: data.stt_destination_city_id,
      sttDestinationCityName: data.stt_destination_city_name,
      sttDestinationCityTransit: data.stt_destination_city_transit,
      sttOriginDistrictId: data.stt_origin_district_id,
      sttDestinationDistrictId: data.stt_destination_district_id,
      sttDestinationDistrictName: data.stt_destination_district_name,
      sttDistrictUrsaCode: data.stt_district_ursa_code || "",
      sttOperationArea: data.operation_area || "",
      sttDistrictType: data.stt_district_type,
      sttDestinationDistrictType: data.stt_destination_district_type,
      sttInformationDistrictType: data.stt_information_district_type,
      sttShipmentSource: data.stt_shipment_source,
      sttSenderName: data.stt_sender_name,
      sttSenderPhone: data.stt_sender_phone,
      sttSenderAddress: data.stt_origin_district_name,
      sttRecipientName: data.stt_recipient_name,
      sttRecipientAddress: data.stt_recipient_address,
      sttRecipientAddressType: data.stt_recipient_address_type,
      sttRecipientPhone: data.stt_recipient_phone,
      sttBookedName: data.stt_booked_name,
      sttNoRefExternal: data.stt_no_ref_external,
      sttCommodityCode: data.stt_commodity_code,
      sttCommodityName: data.stt_commodity_name,
      sttIsCod: data.stt_is_cod,
      sttIsDfod: data.stt_is_dfod,
      sttIsDo: data.stt_is_do,
      sttCodAmount: data.stt_cod_amount,
      sttTotalAmount: data.stt_total_amount_before_discount,
      sttBmTaxRate: data.stt_bm_tax_rate,
      sttPpnTaxRate: data.stt_ppn_tax_rate,
      sttPphTaxRate: data.stt_pph_tax_rate,
      sttShippingSurchargeRate: data.stt_shipping_surcharge_rate,
      sttPublishRate: data.stt_publish_rate,
      sttOriginDistrictRate: data.stt_origin_district_rate,
      sttDestinationDistrictRate: data.stt_destination_district_rate,
      sttDocumentSurchargeRate: data.stt_document_surcharge_rate,
      sttCommoditySurchargeRate: data.stt_commodity_surcharge_rate,
      sttHeavyweightSurchargeRate: data.stt_heavyweight_surcharge_rate,
      sttInsuranceRate: data.stt_is_promo
        ? data.stt_insurance_rate_after_discount
        : data.stt_insurance_rate,
      sttWoodpackingRate: data.stt_woodpacking_rate,
      sttGrossWeight: data.stt_gross_weight,
      sttVolumeWeight: data.stt_volume_weight,
      sttChargeableWeight: data.stt_chargeable_weight,
      sttTotalPiece: data.stt_total_piece,
      estimateSla: data.estimate_sla_max_date,
      sttTotalSurcharge: data.stt_total_surcharge,
      sttAmount: data.stt_total_amount_before_discount,
      sttPieces: convertSnakeToCamel(
        extraReceipt
          ? data.stt_pieces.concat(data.stt_pieces[data.stt_pieces.length - 1])
          : data.stt_pieces
      ),
      sttVendorCode: data.stt_vendor_code,
      sttVendorName: data.stt_vendor_name,
      sttShipmentId: data.stt_shipment_id,
      sttClientId: data.stt_client_id,
      sttClientName: data.stt_client_name,
      sttVendorRefferenceNo: data.stt_vendor_reference_no,
      sttCodFee: data.stt_is_promo
        ? data.stt_cod_fee_after_discount
        : data.stt_cod_fee,
      otherShipperTicketCode:
        data.other_shipper_ticket_code === null
          ? []
          : data.other_shipper_ticket_code.map((item: any) => item.ticket_code),
      sttBookedForName: data.stt_booked_for_name,
      sttForwardRate: 0,
      totalTariff: totalTariff,
      totalRate: totalRates,
      isDangerousGoods: data.is_dangerous_goods,
      sttIsRts: IS_STT_RTS(data.last_status_stt_return),
      sttIsRerouteMisroute:
        data.last_status_stt_return.match(/^reroute/gi) &&
        data.last_status_before_stt_return.match(/^mis-route/gi),
      sttIsRerouteMisbooking:
        data.last_status_stt_return.match(/^reroute/gi) &&
        data.last_status_before_stt_return.match(/^misbooking/gi),
      sttCommodityDescription: data.stt_commodity_description ?? "",
      sttGoodsEstimatePrice: data.stt_goods_estimate_price,
      sttPostalCode: data.stt_postal_code,
      isReverseJourneyAfterCancel: data.is_reverse_journey_after_cancel,
      sttGoodsPrice: data.stt_goods_price,
      sttReturnFee: data.stt_return_fee,
      totalTariffCodRetail: totalTariff,
      isReverseJourney: data.detail_stt_reverse_journey,
      sttReverseJourneySttNo:
        data.detail_stt_reverse_journey?.root_reverse_stt_no ?? "",
      sttReverseJourneyShipmentId:
        data.detail_stt_reverse_journey?.root_reverse_stt_shipment_id ?? "",
      sttReverseJourneyCodHandling: !!data.detail_stt_reverse_journey
        ?.cod_handling,
      sttIsPromo: data.stt_is_promo,
      sttDocumentSurchargeRateAfterDiscount:
        data.stt_document_surcharge_rate_after_discount,
      sttCommoditySurchargeRateAfterDiscount:
        data.stt_commodity_surcharge_rate_after_discount,
      sttHeavyweightSurchargeRateAfterDiscount:
        data.stt_heavyweight_surcharge_rate_after_discount,
      sttWoodpackingAfterRateDiscount: data.stt_woodpacking_rate_after_discount,
      sttBookedByCountry: data.stt_booked_by_country,
      sttIsWoodpacking: data.stt_is_woodpacking,
      isHubToHub:
        data.stt_product_type?.toUpperCase() === "JUMBOPACKH2H" || false,
      sttFlag: data.stt_flag,
      campaignSttQuote: data.campaign_stt_quote,
      sttInsuranceRateAfterDiscount: data.stt_insurance_rate_after_discount,
      sttCodFeeAfterDiscount: data.stt_cod_fee_after_discount,
      sttShippingSurchargeRateAfterDiscount:
        data.stt_shipping_surcharge_rate_after_discount,
      sttPublishRateAfterDiscount: data.stt_publish_rate_after_discount,
      sttOriginDistrictRateAfterDiscount:
        data.stt_origin_district_rate_after_discount,
      sttDestinationDistrictRateAfterDiscount:
        data.stt_destination_district_rate_after_discount,
      isPriorityTier: data.is_priority,
      sttCommodityIsQuarantine: data.stt_commodity_is_quarantine,
      sttIdentityNumber: data.stt_identity_number,
      sttDestinationCountryCode: data.stt_destination_country_code,
      currencyCode: data.currency_code,
      sttCipl: data.stt_cipl,
      sttRecipientEmail: data.stt_recipient_email,
      sttProducts:
        data.stt_products?.map(
          (item: any) =>
            new SttProduct({
              productName: item.product_name,
              quantity: item.quantity,
              variant: item.variant
            })
        ) || [],
      estimateSlaMaxDate: data.estimate_sla_max_date,
      estimateSlaMinDate: data.estimate_sla_min_date,
      bookingId: data.booking_id || "",
      vendorDl: data.vendor_dl
        ? convertSnakeToCamel(data.vendor_dl)
        : undefined,
      isInternational: data.is_international
    });
  }
}
